import React from "react";
import { FormGroup } from "reactstrap";

import { tooltips } from "../../../../../lib/utils/tooltips";

import { Input } from "../../../Input/Input";
import { InformationBox } from "../../../InformationBox/InformationBox";
import { LabelWithInformationBox } from "../../../LabelWithInformationBox/LabelWithInformationBox";
import { RoomSelect } from "../../../../Workplaces/RoomSelect/RoomSelect";
import { WayfinderSelect } from "../../../../Workplaces/WayfinderSelect/WayfinderSelect";
import { EditDeviceFormik } from "../lib/useEditDeviceFormik";
import { DeviceWorkspaceType } from "../EditDeviceForm";
import { SectionHeader } from "../ui/SectionHeader";
import { Notice } from "../ui/Notice";

export interface GeneralSectionStatus {
  pushAvailable: boolean;
  lastSync: string;
}

interface Props {
  formik: EditDeviceFormik;
  isWayfinderDevice: boolean;
  // null when the device is unassigned or has no data yet
  status: GeneralSectionStatus | null;
}

export const GeneralSection = ({
  formik,
  isWayfinderDevice,
  status,
}: Props) => {
  const handleWorkspaceChange = (
    value: string | null,
    type: DeviceWorkspaceType
  ) => {
    formik.setFieldValue(type, value);
    formik.setFieldTouched(type);
  };

  return (
    <section className="DeviceSection GeneralSection">
      <SectionHeader icon="device_icon_18x18" title="General" />

      <FormGroup>
        <LabelWithInformationBox
          for="name"
          title="Device name"
          direction="left"
          message={tooltips.devices.name}
        />
        <Input
          id="name"
          type="text"
          name="name"
          value={formik.values.name}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          isTouched={formik.touched.name}
          errorMessage={formik.errors.name}
          invalid={!!formik.errors.name && formik.touched.name}
        />
      </FormGroup>
      <FormGroup>
        <LabelWithInformationBox
          for="description"
          title="Description"
          direction="left"
          message={tooltips.devices.description}
        />
        <Input
          type="text"
          id="description"
          name="description"
          onBlur={formik.handleBlur}
          value={formik.values.description}
          onChange={formik.handleChange}
        />
      </FormGroup>
      {isWayfinderDevice ? (
        <WayfinderSelect
          value={formik.values.wayFinderId}
          onChange={handleWorkspaceChange}
          isDisabled={!!formik.values.roomId}
        />
      ) : (
        <RoomSelect
          value={formik.values.roomId}
          onChange={handleWorkspaceChange}
          isDisabled={!!formik.values.wayFinderId}
        />
      )}

      {status ? (
        <div className="GeneralSection__status">
          <Notice tone={status.pushAvailable ? "success" : "danger"}>
            {status.pushAvailable
              ? "Push connectivity available"
              : "Push connectivity unavailable"}
          </Notice>
          <div className="DeviceSection__meta">
            <p className="flex-a-center">
              Last status sync: {status.lastSync}
              <InformationBox
                message={tooltips.devices.status}
                className="pl-10"
              />
            </p>
            <p>Status information syncs every 8 hours.</p>
          </div>
        </div>
      ) : (
        <Notice tone="muted">Unassigned or no data found.</Notice>
      )}
    </section>
  );
};
